import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { BreadcrumbService } from './breadcrumb.service';
import { SidebarMenuStateService } from './sidebarmenustateservice';
import { menuMetaConfig } from '../classes/domain/constants';

@Injectable({ providedIn: 'root' })
export class BreadcrumbAutoService {

  constructor(
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    private sidebarMenuState: SidebarMenuStateService
  ) { }

  init() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        this.buildBreadcrumb(e.urlAfterRedirects);
      });
    this.buildBreadcrumb(this.router.url);
  }

  private buildBreadcrumb(url: string) {
    const cleanUrl = url
      .replace('/#/', '')
      .replace('#/', '')
      .replace(/^\//, '');

    // ── CASE 1: Home ──────────────────────────
    if (cleanUrl === '' || cleanUrl.includes('home')) {
      this.breadcrumbService.set([{ label: 'Home', labelKey: 'MENU.Dashboard' }]);
      return;
    }

    // ── CASE 2: Feature Group ─────────────────
    const featureMatch = cleanUrl.match(/Feature-Group\/([^?#]+)/);
    if (featureMatch) {
      const feature = decodeURIComponent(featureMatch[1] || '');
      this.breadcrumbService.set([
        { label: 'Home', labelKey: 'MENU.Dashboard', path: 'home' },
        { label: feature, labelKey: `FEATURE.${feature}` }
      ]);
      return;
    }

    // ── CASE 5: Dashboard pages ───────────────
    const dashboardPaths = [
      'Dashboard_Anemia_Mukta_Bharat',
      'Dashboard_Asha_Incentive',
      'Dashboard_Contraceptive_pills',
      'Asha-Af-Employee-State-Incentive-Report',
    ];
    if (dashboardPaths.includes(cleanUrl)) {
      return;
    }

    // ── CASES 3 & 4: Need menu data ───────────
    //  wait until menu has real data (fixes refresh issue)
    this.sidebarMenuState.menu$
      .pipe(
        filter(menu => menu.length > 0),  //  skip empty array on refresh
        take(1)
      )
      .subscribe(menu => {
        this.buildBreadcrumbWithMenu(cleanUrl, menu);
      });
  }

  private buildBreadcrumbWithMenu(cleanUrl: string, menu: any[]) {

    // ── CASE 4: Menu page ──────────────────────
    const matchedEntry = Object.entries(menuMetaConfig).find(
      ([_, val]) => val.path === cleanUrl
    );

    if (matchedEntry) {
      const [menuId, meta] = matchedEntry;
      const menuItem = menu.find(m => m.MenuId === menuId);

      if (menuItem) {
        // ✅ normalize FeatureGroup key — spaces → underscores
        const featureGroupKey = menuItem.FeatureGroup.replaceAll(' ', '_');

        this.breadcrumbService.set([
          { label: 'Home', labelKey: 'MENU.Dashboard', path: 'home' },
          { label: menuItem.Feature, labelKey: `FEATURE.${menuItem.Feature}`, path: `Feature-Group/${menuItem.Feature}` },
          { label: menuItem.FeatureGroup.replaceAll('_', ' '), labelKey: `FEATURE_GROUP.${featureGroupKey}`, path: `Feature-Group/${menuItem.Feature}` },
          { label: meta.text, labelKey: `MENU.${menuId}` },
        ]);
        this.breadcrumbService.setExpandGroup(menuItem.FeatureGroup);
      }
      return;
    }

    // ── CASE 5: Detail page ────────────────────
    const detailSuffixes = ['_Details', '_Detail', '_Form_Details'];
    let parentUrl = cleanUrl;

    for (const suffix of detailSuffixes) {
      if (cleanUrl.endsWith(suffix)) {
        parentUrl = cleanUrl.slice(0, -suffix.length);
        break;
      }
    }

    const parentEntry = Object.entries(menuMetaConfig).find(
      ([_, val]) => val.path.toLowerCase() === parentUrl.toLowerCase()
    );

    if (parentEntry) {
      const [menuId, meta] = parentEntry;
      const menuItem = menu.find(m => m.MenuId === menuId);

      if (menuItem) {
        // ✅ normalize FeatureGroup key — spaces → underscores
        const featureGroupKey = menuItem.FeatureGroup.replaceAll(' ', '_');

        this.breadcrumbService.set([
          { label: 'Home', labelKey: 'MENU.Dashboard', path: 'home' },
          { label: menuItem.Feature, labelKey: `FEATURE.${menuItem.Feature}`, path: `Feature-Group/${menuItem.Feature}` },
          { label: menuItem.FeatureGroup.replaceAll('_', ' '), labelKey: `FEATURE_GROUP.${featureGroupKey}`, path: `Feature-Group/${menuItem.Feature}` },
          { label: meta.text, labelKey: `MENU.${menuId}`, path: meta.path },
          { label: `${meta.text} Details`, labelKey: `MENU.${menuId}` },
        ]);
        this.breadcrumbService.setExpandGroup(menuItem.FeatureGroup);
      }
      return;
    }

    // ── Fallback ───────────────────────────────
    this.breadcrumbService.set([
      { label: 'Home', labelKey: 'MENU.Dashboard', path: 'home' }
    ]);
  }
}